<template>
  <div
    v-if="isLogin"
    class="relative h-full"
    style="min-height: 95vh"
  >
    <div
      class="custom-loading"
    />
    <div class="initial-login-version">
      Powered by&nbsp;<span class="font-500">客立樂Pro</span><br>
      {{ computedVersion }}
    </div>
  </div>
  <div
    v-else
    class="body-scroll-container login-container"
  >
    <div class="login-page-header">
      <div>
        <icon-button
          v-if="route.query?.redirectUri === 'profile' ? !!route.query?.from : true"
          class="white-button"
          icon-type="button-close"
          @click="onClickCloseBtn()"
        />
      </div>
    </div>
    <div style="min-height: 100%;">
      <!-- 輸入電話號碼 -->
      <div
        v-if="stepName === 'phone-input-form'"
        class="phone-input-form p-3"
      >
        <div class="section-title mb-12">
          嗨！請加入您的號碼繼續預約。
        </div>
        <div class="section-description mb-4">
          填入號碼後點選「登入/註冊」按鈕，即代表您已同意服務條款與隱私權政策。
        </div>
        <div class="mb-3">
          <form-template
            :cols="phoneCols"
            :data="userInfo"
            type="loginPhone"
          />
          <div
            v-if="errorMsg"
            class="mt-2 text-[13px] leading-[21px] text-red pl-3"
          >
            {{ errorMsg }}
          </div>
        </div>
        <div class="mb-2">
          <el-button
            class="next-step-button w-full"
            :disabled="isLoading || isNoValidated"
            type="primary"
            @click="sendCustomerCheck"
          >
            <span v-if="isLoading">載入中...</span>
            <span v-else>登入 / 註冊</span>
          </el-button>
        </div>
      </div>
    </div>
  </div>
  <custom-message-box
    ref="customMessageBoxRef"
    :buttons="messageBoxData.buttons"
    :description="messageBoxData.description"
    :title="messageBoxData.title"
  />
</template>

<script>
const phoneCols = [
  {
    id: 'phone',
    type: 'phone',
    placeholder: '輸入電話號碼',
    rules: [
      { required: true, message: '電話號碼為必填項目', trigger: 'blur' },
      {
        validator: (_rule, value, callback) => {
          if (value) {
            if (!isValidPhoneNumber(value.number, value.code)) {
              return callback(new Error('請輸入正確的電話格式'))
            }
          }
          callback()
        },
        trigger: ['blur', 'change'],
      },
    ],
  },
]
</script>

<script setup>
import liff from '@line/liff'
import { get } from 'lodash-es'

import { usePhoneNumberFormat } from '@/composables/usePhoneNumberFormat'
import { useStoreCname } from '@/composables/useStoreCname'
import {
  closeLoginFlowPage,
  getPageQueryAndParams,
  getSessionStorage,
  getUserLocalStorage,
  sendDatadogLog,
  sendMixpanelEvent,
  setSessionStorage,
} from '@/utils'
import { getPhoneNumberType } from '@/utils/getPhoneNumberType'
import { getRegionCodeFromE164 } from '@/utils/getRegionCodeFromE164'
import { isValidPhoneNumber } from '@/utils/isValidPhoneNumber'

const route = useRoute()
const router = useRouter()
const store = useStore()
const queryObj = computed(() => route.query)
const storeCname = useStoreCname()
const designerId = computed(() => get(queryObj.value, 'userId', null))
const computedVersion = computed(() => `v${import.meta.env.VITE_APP_VERSION}`)
const stepName = ref('phone-input-form')
const isLogin = ref(true)
const isLoading = ref(false)
const errorMsg = ref('')
const messageBoxData = reactive({
  title: '錯誤訊息',
  description: '',
  buttons: {
    submit: { show: true, text: '關閉', type: 'danger', fn: () => {} },
    cancel: { show: false, text: '', type: '', fn: () => {} },
    extra: { show: false, text: '', type: '', fn: () => {} },
  },
})

const customMessageBoxRef = ref(null)

const userInfo = ref({
  phone: {
    code: 'TW',
    number: '',
  },
})

const userInfoFinal = ref()
watch(
  () => userInfo.value,
  value => {
    userInfoFinal.value = {
      ...value,
      phone: phoneNumberFormatted.value,
    }
  },
  { deep: true },
)

const isNoValidated = computed(() => {
  const { number, code } = userInfo.value.phone
  if (number.length > 0) {
    const numberType = getPhoneNumberType(number, code)
    const isValidType =
      numberType === 'MOBILE' || numberType === 'FIXED_LINE_OR_MOBILE'

    return !(isValidPhoneNumber(number, code) && isValidType)
  }
  return true
})

const phoneNumberFormatted = usePhoneNumberFormat(() => ({
  phone: userInfo.value.phone.number,
  region: userInfo.value.phone.code,
  format(phoneNumber) {
    const display =
      `${phoneNumber.getCountryCode() ?? ''} ${phoneNumber.getNationalNumber() ?? ''}`.trim()
    return display ? `+${display}` : ''
  },
}))

const onClickCloseBtn = () => {
  const { routeName, query, params } = closeLoginFlowPage({
    query: route.query,
    cname: storeCname.value,
  })
  router.push({ name: routeName, query, params })
}

const liffInfo = {
  originLineUserId: '',
  originLineAvatar: '',
}

// api
const getCustomerLineUserId = async () => {
  const result = await store.dispatch('auth/customerCheck', {
    lineToken: liff.getIDToken(),
    code: storeCname.value,
  })
  if (result) {
    const {
      status = '',
      exists = false,
      lineUserId = '',
      lineAvatar = '',
    } = result
    if (lineUserId) {
      const loginStorage = getSessionStorage(`Login-${storeCname.value}`)
      liffInfo.originLineUserId = lineUserId
      liffInfo.originLineAvatar = lineAvatar
      setSessionStorage(`Login-${storeCname.value}`, {
        ...loginStorage,
        lineUserId,
        lineAvatar,
      })
    }
  }
}

const sendCustomerCheck = async () => {
  const isLiffBrowser = liff.isInClient()
  const phoneE164 = phoneNumberFormatted.value.replaceAll(' ', '')
  sendMixpanelEvent({
    title: '線上預約_點按登入/註冊',
    data: { code: getRegionCodeFromE164(phoneE164) },
  })

  // 手機登入或是 LINE 登入 二擇一
  const result = await store.dispatch('auth/customerCheck', {
    code: storeCname.value,
    phoneE164,
  })

  const { status, exists = false, lineUserId = '', lineAvatar = '' } = result
  const { originLineUserId, originLineAvatar } = liffInfo
  /*
    前端需自行比對 lineUserId 是否有變更，若有變更則擋下登入
    若任一為空字串，則尚未綁定 LINE
  */
  if (
    isLiffBrowser &&
    lineUserId &&
    originLineUserId &&
    lineUserId !== originLineUserId
  ) {
    Object.assign(messageBoxData, {
      title: '電話號碼已綁定 LINE',
      description: '此號碼已綁定 LINE，請更換號碼或聯絡店家變更。',
      buttons: {
        submit: {
          show: true,
          text: '確認',
          type: 'primary',
          fn: () => {
            customMessageBoxRef?.value.closePopup()
          },
        },
        cancel: { show: false, text: '', type: '', fn: () => {} },
        extra: { show: false, text: '', type: '', fn: () => {} },
      },
    })
    customMessageBoxRef?.value.openPopup()
    return
  }

  // 儲存登入手機及 LINE 資訊
  setSessionStorage(`Login-${storeCname.value}`, {
    ...(isLiffBrowser
      ? {
          lineUserId: lineUserId || originLineUserId,
          lineAvatar: lineAvatar || originLineAvatar,
        }
      : null),
    phone: userInfo.value.phone,
    code: userInfo.value.code,
  })

  switch (status) {
    case 'SUCCESS':
      // 已設定密碼的註冊會員，跳轉至密碼登入頁
      router.push({ name: 'LoginPassword', query: queryObj.value })
      break
    case 'CUSTOMER_PASSWORD_NOT_SET':
      // 已存在的註冊會員，但未設定密碼，跳轉至設定密碼頁
      router.push({ name: 'SetPassword', query: queryObj.value })
      break
    case 'CUSTOMER_PASSWORD_RESET':
      // 已存在的註冊會員，但需重設密碼，跳轉至重設密碼頁
      router.push({ name: 'ForgetPassword', query: queryObj.value })
      break
    case 'RESOURCE_NOT_FOUND':
    case 'CUSTOMER_NEED_REGISTER':
      // 非會員，跳轉至註冊頁 | B端已建立的顧客
      router.push({ name: 'Register', query: queryObj.value })
      break
    case 'WRONG_FORMAT':
      errorMsg.value = '電話格式錯誤'
      break
    case 'PASSWORD_NOT_MATCH':
      errorMsg.value = '密碼錯誤'
      break
    case 'CHECK_API_FAILED':
    default:
      errorMsg.value = '驗證失敗'
      break
  }
}

const initData = async () => {
  const result = await store.dispatch('store/getStore', {
    code: storeCname.value,
    ...(designerId.value ? { userId: designerId.value } : null),
  })
  if (result?.status === 'SUCCESS') {
    const { liffId } = result.data
    const isLiffBrowser = liffId && liff.isInClient()
    if (isLiffBrowser) {
      await liff.init({ liffId }).catch(err => {
        sendDatadogLog({
          message: 'UserLogin Page - LIFF SDK 初始化失敗',
          data: { code: err.code, message: err.message },
          status: 'info',
        })
      })
      if (!liff.isLoggedIn()) {
        router.push({ name: 'LiffRedirect', query: queryObj.value })
        return
      }
    }
    const authData = getUserLocalStorage({ cname: storeCname.value })
    if (!authData) {
      const loginStorage = getSessionStorage(`Login-${storeCname.value}`)
      if (loginStorage) {
        if (isLiffBrowser) {
          // 若從 LIFF 進入，需取得 lineUserId 進行比對
          liffInfo.originLineUserId = loginStorage?.lineUserId
          liffInfo.originLineAvatar = loginStorage?.lineAvatar
        } else {
          userInfo.value.phone.code = loginStorage.phone.code
          userInfo.value.phone.number = loginStorage.phone.number
        }
      } else if (!loginStorage && isLiffBrowser) {
        // 若從 liff.line.me 連結進入，不會經過 LiffRedirectPage
        await getCustomerLineUserId()
      }
      sendMixpanelEvent({ title: '線上預約_進入輸入電話號碼頁', data: {} })
      isLogin.value = false
    } else {
      const { routeName, query, params } = getPageQueryAndParams({
        query: queryObj.value,
      })
      router.push({ name: routeName, query, params })
    }
  } else if (result?.status === 'RESOURCE_NOT_FOUND') {
    router.push({ name: 'NotFound', query: queryObj.value })
  } else {
    Object.assign(messageBoxData, {
      title: '暫不接受預約',
      description: '若有任何預約問題請與商店聯繫，將有專人為您服務',
      buttons: {
        submit: {
          show: true,
          text: '確認',
          type: 'primary',
          fn: () => {
            window.location.reload()
          },
        },
        cancel: { show: false, text: '', type: '', fn: () => {} },
        extra: { show: false, text: '', type: '', fn: () => {} },
      },
    })
    customMessageBoxRef?.value.openPopup()
  }
}

onMounted(() => initData())
</script>
